"use client";

import type { PlainDate } from "@sa-nu/shared-utils/date";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { AccommodationReservationProviderInput } from "../../../__generated__/gql/graphql";

import { RoomDetailDrawer } from "./room-detail-drawer";

/**
 * ネストした Drawer を描画する render prop に渡す引数。
 * 親 (部屋詳細 Drawer) が open 状態を管理する。
 */
export type NestedDrawerRenderArgs = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ネスト Drawer と部屋詳細 Drawer をまとめて閉じる */
  closeAll: () => void;
  /**
   * PC Popover のアンカー要素 (「予約する」CTA)。Popover の `anchor` prop に渡す。
   * room-detail 側が callback ref で取得した state 要素をそのまま渡す
   * (RefObject ではなく要素なので React Compiler の ref ルールに抵触しない)。
   */
  anchorEl: HTMLElement | null;
};

/**
 * Drawer を開くときに必要な情報。部屋を 1 室 (accommodation) で開くか、
 * 部屋タイプ (roomType) で開くかの 2 経路を判別 union で表す。
 *
 * - accommodation 経路: `Query.accommodation(id:)` で部屋情報を fetch。
 *   `availability` がある場合は「予約する」CTA を出し、`availabilities` を合わせて
 *   fetch して価格 / 日付を CTA に表示。CTA 押下で `nestedDrawer`
 *   (部屋詳細を閉じずに重ねるチェックアウト日選択 Drawer 等) を開く。
 *   `availability` が無い場合は「閉じる」CTA (browsing 用)。
 * - roomType 経路: `Query.roomType(id:)` で部屋タイプ情報を fetch。満室でも開ける。
 *   `reservation` がある場合は `inlineContent` を常時描画し、無い場合は「閉じる」CTA (browsing 用)。
 */
export type RoomDetailDrawerPayload =
  | AccommodationRoomDetailPayload
  | RoomTypeRoomDetailPayload;

type RoomDetailReserveAvailability = {
  firstNight: PlainDate;
  provider: AccommodationReservationProviderInput;
  /** CTA 押下で部屋詳細に重ねて開くネスト Drawer (チェックアウト日選択等) の render prop */
  nestedDrawer: {
    render: (args: NestedDrawerRenderArgs) => ReactNode;
  };
};

export type AccommodationRoomDetailPayload = {
  accommodationId: string;
  availability?: RoomDetailReserveAvailability;
};

export type InlineContentRenderArgs = {
  /** インラインコンテンツ内の操作（日程確定等）で部屋詳細ごと閉じる */
  closeAll: () => void;
};

export type RoomTypeRoomDetailPayload = {
  roomTypeId: string;
  reservation?: {
    /** room-detail を予約ロジックから切り離すため、呼び出し側が組み立てたノードを受け取って描画するだけにする */
    inlineContent: { render: (args: InlineContentRenderArgs) => ReactNode };
  };
};

type RoomDetailDrawerActions = {
  open: (payload: RoomDetailDrawerPayload) => void;
  close: () => void;
};

const RoomDetailDrawerContext = createContext<RoomDetailDrawerActions | null>(
  null,
);

export function useRoomDetailDrawer(): RoomDetailDrawerActions {
  const ctx = useContext(RoomDetailDrawerContext);
  if (!ctx)
    throw new Error(
      "useRoomDetailDrawer must be used within RoomDetailDrawerProvider",
    );
  return ctx;
}

export function RoomDetailDrawerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [payload, setPayload] = useState<RoomDetailDrawerPayload | null>(null);

  const close = useCallback(() => setPayload(null), []);

  const actions = useMemo<RoomDetailDrawerActions>(
    () => ({
      open: (p) => setPayload(p),
      close,
    }),
    [close],
  );

  return (
    <RoomDetailDrawerContext.Provider value={actions}>
      {children}
      <RoomDetailDrawer payload={payload} onClose={close} />
    </RoomDetailDrawerContext.Provider>
  );
}
